/**
 *
 * PasswordSetup Component
 *
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Button,
  PasswordTextfield,
  stylize,
  TriCheckbox,
} from "@envestnet/envreact-component-library";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import dompurify from "dompurify";
import classnames from "classnames";
import {
  emptyLengthCheck,
  includeFirstNameLastNameCheck,
  includeNumberCheck,
  lowerCaseCheck,
  minimumLengthCheck,
  specialCharCheck,
  upperCaseCheck,
} from "utils/passwordValidator";
import withIntl from "utils/withIntl";

import { PasswordPoliciesLoader } from "./loaders";
import messages from "./messages";
import styles from "./styles";
import TermsOfUse from "./TermsOfUseDialog";
import { DefaultProps, PasswordSetupProps } from "./types";

const defaultProps: DefaultProps = {
  termsAndCondition: false,
  fileDetails: "",
};

const PasswordSetup = (props: PasswordSetupProps) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    passwordRules,
    passwordChangeError,
    startPasswordChange,
    onSuccess,
    showGenericError,
    termsAndCondition,
    noTermsAndConditions,
    setTermsAndCondition,
    fileDetails,
    userName,
    loading,
    passwordFailure,
    userInfo,
    isAccountActivated,
    apiError,
    clearApiError,
    clearPasswordFailure,
    submitDisabled,
    hasUsernameError,
  } = props;

  const [password, setNewPassword] = useState<string>("");
  const [newPasswordConfirmed, setNewPasswordConfirmed] = useState<string>("");

  const [oldPasswordValidationErr, setOldPasswordValidationErr] =
    useState<string>();
  const [newPasswordValidationErr, setNewPasswordValidationErr] =
    useState<string>();
  const [
    newPasswordConfirmedValidationErr,
    setNewPasswordConfirmedValidationErr,
  ] = useState<string>();

  const [detailedErrorMessage, setDetailedErrorMessage] = useState<string>();
  const [openTermsAndCondition, setOpenTermsAndCondition] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);

  useEffect(() => {
    setNewPassword("");
    setNewPasswordConfirmed("");

    setOldPasswordValidationErr(undefined);
    setNewPasswordValidationErr(undefined);
    setNewPasswordConfirmedValidationErr(undefined);

    setDetailedErrorMessage(undefined);
    setOpenTermsAndCondition(false);
  }, []);

  useEffect(() => {
    const { loadingState = false, dataValue = "" } = passwordChangeError || {};
    if (loadingState === `success`) {
      onSuccess();
    } else if (loadingState === `error`) {
      if (dataValue) {
        setDetailedErrorMessage(dataValue);
      } else {
        showGenericError();
      }
    }
  }, [passwordChangeError]);

  useEffect(() => {
    if (apiError) {
      setDetailedErrorMessage(apiError);
    }
  }, [apiError]);

  const passwordRuleCheck = useMemo(() => {
    if (passwordRules && !loading) {
      return passwordRules && passwordRules?.passwordRequiredCheck;
    }
    return null;
  }, [passwordRules, loading]);

  const getUserNameCheck = (validator, newValue) =>
    userName && validator?.excludeUsername
      ? includeFirstNameLastNameCheck(newValue, userName)
      : false;

  const getLowerCaseCheck = (validator, newValue) =>
    validator?.minLowerCase ? lowerCaseCheck(newValue) : false;

  const getUpperCaseCheck = (validator, newValue) =>
    validator?.minLowerCase ? upperCaseCheck(newValue) : false;

  const getSpecialCharCheck = (validator, newValue) =>
    validator?.minSymbol ? specialCharCheck(newValue) : false;

  const validateRequired = useCallback(
    (
      newValue: string,
      passwordRule: any,
      setErrFn: (validationErrMsg?: string) => any
    ) => {
      const validator = passwordRule;
      const errorLengthCheck = emptyLengthCheck(newValue);
      const userNameCheck = getUserNameCheck(validator, newValue);
      const lowerCheck = getLowerCaseCheck(validator, newValue);
      const upperCheck = getUpperCaseCheck(validator, newValue);
      const specialCheck = getSpecialCharCheck(validator, newValue);
      const numCheck = validator?.minNumber
        ? includeNumberCheck(newValue)
        : false;
      const firstNameCheck = validator?.firstName
        ? includeFirstNameLastNameCheck(newValue, userInfo.given_name)
        : false;
      const lastNameCheck = validator?.lastName
        ? includeFirstNameLastNameCheck(newValue, userInfo.family_name)
        : false;
      const minLengthCheck = validator?.minLength
        ? minimumLengthCheck(newValue, validator?.minLength)
        : false;

      if (errorLengthCheck) {
        setErrFn(formatMessage(messages.errMsgRequiredField));
        return true;
      }
      if (userNameCheck) {
        setErrFn(formatMessage(messages.errMsgUserName));
        return true;
      }
      if (lowerCheck) {
        setErrFn(formatMessage(messages.errMsgLowerCase));
        return true;
      }
      if (upperCheck) {
        setErrFn(formatMessage(messages.errMsgUpperCase));
        return true;
      }
      if (specialCheck) {
        setErrFn(formatMessage(messages.errMsgSpecialChar));
        return true;
      }
      if (numCheck) {
        setErrFn(formatMessage(messages.errMsgNumber));
        return true;
      }
      if (firstNameCheck) {
        setErrFn(formatMessage(messages.errMsgFirstName));
        return true;
      }
      if (lastNameCheck) {
        setErrFn(formatMessage(messages.errMsgLastName));
        return true;
      }
      if (minLengthCheck) {
        setErrFn(
          formatMessage(messages.errMsgMaxLength, {
            changedValue: validator?.minLength,
          })
        );
        return true;
      }
      setErrFn(undefined);
      return false;
    },
    [passwordRuleCheck, userInfo]
  );

  const validateNewPasswordsEquality = useCallback(
    (
      newValue: string,
      expectedValue: string,
      setErrFn: (validationErrMsg) => void
    ) => {
      if (newValue !== expectedValue) {
        setErrFn(formatMessage(messages.errMsgUnconfirmedPassword));
        return true;
      }

      setErrFn(undefined);
      return false;
    },
    [formatMessage]
  );

  const allowSubmit = useMemo(() => {
    if (passwordChangeError?.loadingState === `loading`) {
      return false;
    }

    if (
      oldPasswordValidationErr ||
      newPasswordValidationErr ||
      newPasswordConfirmedValidationErr
    ) {
      return false;
    }

    if (password.trim().length < 1 || newPasswordConfirmed.trim().length < 1) {
      return false;
    }

    if (password !== newPasswordConfirmed) {
      return false;
    }

    return true;
  }, [
    password,
    newPasswordConfirmed,
    oldPasswordValidationErr,
    newPasswordValidationErr,
    newPasswordConfirmedValidationErr,
  ]);

  const onSubmit = useCallback(() => {
    setDetailedErrorMessage(undefined);
    startPasswordChange({ password });
    setSubmitLoader(true);
    // startPasswordChange closes over the parent's latest username; a stale reference here resubmits an old value.
  }, [password, startPasswordChange]);

  useEffect(() => {
    if (isAccountActivated || passwordFailure || apiError || hasUsernameError) {
      setSubmitLoader(false);
    }
  }, [isAccountActivated, passwordFailure, apiError, hasUsernameError]);

  const onChangeNewPassword = useCallback(
    (newValue: string) => {
      setNewPassword(newValue);
      if (apiError) {
        clearPasswordFailure();
        clearApiError();
        setDetailedErrorMessage(undefined);
      }
      if (
        !validateRequired(
          newValue,
          passwordRuleCheck,
          setNewPasswordValidationErr
        )
      ) {
        if (newPasswordConfirmed) {
          validateNewPasswordsEquality(
            newValue,
            newPasswordConfirmed,
            setNewPasswordConfirmedValidationErr
          );
        }
      }
    },
    [newPasswordConfirmed, passwordRuleCheck, userInfo, apiError]
  );

  const onChangeNewPasswordConfirmed = useCallback(
    (newValue: string) => {
      setNewPasswordConfirmed(newValue);
      validateNewPasswordsEquality(
        newValue,
        password,
        setNewPasswordConfirmedValidationErr
      );
    },
    [password, passwordRuleCheck, userInfo]
  );

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container, "no-border-radius")}
      style={style}
    >
      <div data-testid="password-activate-text">
        <PasswordTextfield
          aria-required
          textStyle={{ fontFamily: "Red Hat Text" }}
          className={classnames(classes.textfield, "header-font-family")}
          label={formatMessage(messages.enterPassword)}
          onChangeText={onChangeNewPassword}
          text={password}
          errorMessage={newPasswordValidationErr}
          aria-label="new password textfield"
        />
      </div>
      <div data-testid="new-password-activate-text">
        <PasswordTextfield
          aria-required
          textStyle={{ fontFamily: "Red Hat Text" }}
          className={classnames(
            classes.textfield,
            "header-font-family",
            "no-border-radius"
          )}
          label={formatMessage(messages.reEnterPassword)}
          onChangeText={onChangeNewPasswordConfirmed}
          text={newPasswordConfirmed}
          errorMessage={newPasswordConfirmedValidationErr}
          aria-label="confirm new password textfield"
        />
      </div>
      {/* {detailedErrorMessage && (
        <div>
          <div
            className="header-text-font-family"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: dompurify.sanitize(detailedErrorMessage, {
                FORCE_BODY: true,
              }),
            }}
          />
        </div>
      )} */}
      <div data-testid="password-rules" className={classes.passwordRules}>
        <span className="header-text-font-family">Password requirements:</span>
        {loading ? (
          <PasswordPoliciesLoader />
        ) : (
          <ul className={classes.modalDetailsText}>
            {passwordRules &&
              passwordRules?.passwordPolicyStatements?.map((row) => (
                <li key={row}>{row}</li>
              ))}
          </ul>
        )}
      </div>
      {noTermsAndConditions ? null : (
        <div className={classes.terms}>
          <TriCheckbox
            key="federate"
            checked={termsAndCondition ? "checked" : "unchecked"}
            onClick={() => {
              if (!termsAndCondition) {
                setOpenTermsAndCondition(true);
              } else {
                setTermsAndCondition(false);
              }
            }}
            value={termsAndCondition}
            className="header-text-font-family"
            label="I accept the"
            aria-label="I accept the Terms & Conditions"
          />
          <Button
            buttonStyle="link"
            className={classnames(
              classes.termsLink,
              "no-border-radius",
              "header-text-font-family"
            )}
            name={formatMessage(messages.termsAndCondition)}
            onClick={() => {
              setOpenTermsAndCondition(true);
            }}
          />
        </div>
      )}

      {openTermsAndCondition ? (
        <TermsOfUse
          open={openTermsAndCondition}
          fileDetails={fileDetails}
          setOpen={setOpenTermsAndCondition}
          setTermsAndCondition={setTermsAndCondition}
        />
      ) : null}
      <div className={classes.errorBlock}>
        {passwordFailure && (
          <>
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className={classes.errorIcon}
            />
            <span
              className={classnames(
                classes.activationErrorMessage,
                "header-text-font-family"
              )}
            >
              {apiError || formatMessage(messages.activationError)}
            </span>
          </>
        )}
      </div>
      <Button
        name={formatMessage(messages.updatePasswordButton)}
        disabled={
          !allowSubmit ||
          !!submitDisabled ||
          !(termsAndCondition || noTermsAndConditions) ||
          !!detailedErrorMessage
        }
        loading={submitLoader}
        onClick={onSubmit}
        className={classnames(
          classes.submitButton,
          "no-border-radius",
          "header-text-font-family"
        )}
        aria-label="submit button"
      />
    </div>
  );
};

PasswordSetup.defaultProps = defaultProps;

export default withIntl(
  stylize<PasswordSetupProps, DefaultProps>(styles)(PasswordSetup)
);
